<template>
  <div class="animated fadeIn lunch-registration-list-page">
    <div class="card">
      <div class="main-card-body">
        <div class="body-header lunch-list-header">
          <div class="lunch-list-header__content">
            <h4 class="card-header-title">DANH SÁCH ĐĂNG KÝ CƠM TRƯA</h4>
            <p class="lunch-list-subtitle">
              Theo dõi đăng ký cơm của nhân viên theo phòng ban và theo tháng
            </p>
          </div>
          <div class="lunch-list-header__actions">
            <button
              type="button"
              class="lunch-primary-button lunch-primary-button--outline"
              @click="goBackToRegistration"
            >
              <i class="fa fa-arrow-left"></i>
              <span>Quay lại</span>
            </button>
            <button
              type="button"
              class="lunch-primary-button"
              :disabled="loading || exporting"
              @click="openExportTypeDialog"
            >
              <i :class="exporting ? 'fa fa-spinner fa-spin' : 'fa fa-file-excel-o'"></i>
              <span>{{ exporting ? "Đang xuất..." : "Xuất Excel" }}</span>
            </button>
          </div>
        </div>

        <section v-loading="loading" class="lunch-list-card">
          <div class="lunch-section-filter">
            <div class="lunch-section-filter__field">
              <label>Tổng hợp theo</label>
              <el-select
                :value="summaryPeriod"
                class="lunch-section-select"
                @change="selectSummaryPeriod"
              >
                <el-option label="Ngày" :value="SUMMARY_BY_DAY" />
                <el-option label="Tháng" :value="SUMMARY_BY_MONTH" />
              </el-select>
            </div>
            <div class="lunch-section-filter__field lunch-section-filter__field--department">
              <label>Phòng ban</label>
              <el-select
                :value="selectedDepartmentId"
                filterable
                class="lunch-section-select"
                @change="selectDepartment"
              >
                <el-option label="Tất cả phòng ban" :value="ALL_DEPARTMENTS" />
                <el-option
                  v-for="department in departments"
                  :key="department.id"
                  :label="department.name"
                  :value="department.id"
                />
              </el-select>
            </div>
          </div>

          <div class="lunch-list-toolbar">
            <div class="lunch-mode-switch">
              <button
                v-for="mode in currentModes"
                :key="mode.value"
                type="button"
                class="lunch-mode-button"
                :class="{ 'lunch-mode-button--active': currentMode === mode.value }"
                @click="setCurrentMode(mode.value)"
              >
                {{ mode.label }}
              </button>
            </div>

            <div class="lunch-list-filters">
              <el-date-picker
                v-if="isSummaryTab"
                v-model="selectedMonth"
                type="month"
                format="MM/yyyy"
                value-format="yyyy-MM"
                placeholder="Chọn tháng"
                class="lunch-month-picker"
              />
              <el-date-picker
                v-else
                v-model="selectedDate"
                type="date"
                format="dd/MM/yyyy"
                value-format="yyyy-MM-dd"
                placeholder="Chọn ngày"
                :clearable="false"
                class="lunch-month-picker lunch-date-picker"
              />
              <el-input
                v-if="isDepartmentTab && currentMode === DEPARTMENT_DAILY_MODE"
                v-model="searchKeyword"
                clearable
                class="lunch-employee-search"
                placeholder="Tìm tên / mã nhân viên..."
                prefix-icon="el-icon-search"
              />
            </div>
          </div>

          <div v-if="!hasMonthData" class="lunch-list-empty">
            Chưa có dữ liệu đăng ký cơm trong tháng này.
          </div>

          <template v-else>
            <div
              v-if="isDepartmentTab && currentMode === DEPARTMENT_DAILY_MODE"
              class="lunch-data-grid-scroll lunch-daily-employee-scroll"
              :class="{ 'has-scroll': dailyEmployeeCount > 8 }"
            >
              <div class="lunch-daily-summary">
                <span>Ngày: <strong>{{ formatDate(selectedDate) }}</strong></span>
                <span>Phòng ban: <strong>{{ selectedDepartmentLabel }}</strong></span>
                <span>
                  Tổng số người đăng ký:
                  <strong>{{ dailyViewData.registrationCount }}</strong>
                </span>
              </div>
              <table class="lunch-data-grid lunch-daily-export-grid">
                <thead>
                  <tr>
                    <th>STT</th>
                    <th>Phòng ban</th>
                    <th>Họ tên</th>
                    <th>Món đăng ký</th>
                    <th>Ghi chú</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!dailyDisplayRows.length">
                    <td colspan="5" class="lunch-table-empty-cell">
                      Không có đăng ký cơm phù hợp trong ngày này.
                    </td>
                  </tr>
                  <tr
                    v-for="item in dailyDisplayRows"
                    :key="item.key"
                    :class="{ 'department-separator-row': item.isSeparator }"
                  >
                    <td v-if="item.isSeparator" colspan="5"></td>
                    <template v-else>
                      <td>{{ item.index }}</td>
                      <td class="text-left">{{ item.row.departmentName }}</td>
                      <td class="text-left">{{ item.row.employeeName }}</td>
                      <td class="text-left food-text-cell">{{ item.row.foodText }}</td>
                      <td class="text-left">{{ item.row.note || "-" }}</td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>

            <div
              v-if="isDepartmentTab && currentMode === DEPARTMENT_DETAIL_MODE"
              class="lunch-data-grid-scroll lunch-daily-food-scroll"
            >
              <table class="lunch-data-grid lunch-daily-food-grid">
                <thead>
                  <tr>
                    <th>STT</th>
                    <th>Tên món ăn</th>
                    <th>Số lượng</th>
                    <th>Tổng tiền</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!dailyFoodRows.length">
                    <td colspan="4" class="lunch-table-empty-cell">
                      Chưa có dữ liệu món ăn trong ngày này.
                    </td>
                  </tr>
                  <tr
                    v-for="(row, index) in dailyFoodRows"
                    :key="row.key"
                  >
                    <td>{{ index + 1 }}</td>
                    <td class="text-left">{{ row.name }}</td>
                    <td>{{ row.quantity }}</td>
                    <td class="text-right">{{ formatCurrency(row.totalAmount) }}</td>
                  </tr>
                </tbody>
                <tfoot v-if="dailyFoodRows.length">
                  <tr>
                    <td colspan="2">TỔNG</td>
                    <td>{{ dailyFoodTotals.quantity }}</td>
                    <td class="text-right">
                      {{ formatCurrency(dailyFoodTotals.totalAmount) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div
              v-if="isSummaryTab && currentMode === SUMMARY_DEPARTMENT_MODE"
              class="lunch-data-grid-scroll"
            >
              <table class="lunch-data-grid lunch-summary-grid">
                <thead>
                  <tr>
                    <th>STT</th>
                    <th>Phòng ban</th>
                    <th>Lượt đăng ký</th>
                    <th>Số lượng món</th>
                    <th>Tổng tiền</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!summaryExportData.departmentRows.length">
                    <td colspan="5" class="lunch-table-empty-cell">
                      Chưa có dữ liệu đăng ký cơm trong tháng này.
                    </td>
                  </tr>
                  <tr
                    v-for="(row, index) in summaryExportData.departmentRows"
                    :key="row.name"
                  >
                    <td>{{ index + 1 }}</td>
                    <td class="text-left">{{ row.name }}</td>
                    <td>{{ row.registrationCount }}</td>
                    <td>{{ row.totalQuantity }}</td>
                    <td class="text-right">{{ formatCurrency(row.totalAmount) }}</td>
                  </tr>
                </tbody>
                <tfoot v-if="summaryExportData.departmentRows.length">
                  <tr>
                    <td colspan="2">TỔNG</td>
                    <td>{{ summaryExportData.totals.registrationCount }}</td>
                    <td>{{ summaryExportData.totals.totalQuantity }}</td>
                    <td class="text-right">
                      {{ formatCurrency(summaryExportData.totals.totalAmount) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div
              v-if="isSummaryTab && currentMode === SUMMARY_FOOD_MODE"
              class="lunch-data-grid-scroll"
            >
              <table class="lunch-data-grid lunch-summary-food-grid">
                <thead>
                  <tr>
                    <th>STT</th>
                    <th>Món ăn</th>
                    <th>Tổng số lượng</th>
                    <th>Tổng tiền</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!summaryExportData.foodRows.length">
                    <td colspan="4" class="lunch-table-empty-cell">
                      Chưa có dữ liệu món ăn trong tháng này.
                    </td>
                  </tr>
                  <tr
                    v-for="(row, index) in summaryExportData.foodRows"
                    :key="row.name"
                  >
                    <td>{{ index + 1 }}</td>
                    <td class="text-left">{{ row.name }}</td>
                    <td>{{ row.totalQuantity }}</td>
                    <td class="text-right">{{ formatCurrency(row.totalAmount) }}</td>
                  </tr>
                </tbody>
                <tfoot v-if="summaryExportData.foodRows.length">
                  <tr>
                    <td colspan="2">TỔNG</td>
                    <td>{{ summaryExportData.totals.totalQuantity }}</td>
                    <td class="text-right">
                      {{ formatCurrency(summaryExportData.totals.totalAmount) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </template>
        </section>
      </div>
    </div>

    <el-dialog
      title="XUẤT EXCEL"
      :visible.sync="exportTypeDialogVisible"
      width="480px"
      custom-class="lunch-list-dialog export-type-dialog"
    >
      <div class="export-type-options">
        <button
          type="button"
          class="export-type-option"
          :class="{ 'is-selected': selectedExportType === 'summary' }"
          :aria-pressed="selectedExportType === 'summary'"
          @click="selectExportType('summary')"
        >
          <i class="fa fa-bar-chart"></i>
          <span class="export-type-option__content">
            <strong>Xuất theo tháng</strong>
            <small>Xuất báo cáo tổng hợp của một tháng cụ thể.</small>
          </span>
          <span class="export-type-option__check">
            <i v-if="selectedExportType === 'summary'" class="fa fa-check"></i>
          </span>
        </button>
        <button
          type="button"
          class="export-type-option"
          :class="{ 'is-selected': selectedExportType === 'daily' }"
          :aria-pressed="selectedExportType === 'daily'"
          @click="selectExportType('daily')"
        >
          <i class="fa fa-calendar-o"></i>
          <span class="export-type-option__content">
            <strong>Xuất theo ngày</strong>
            <small>Xuất danh sách đăng ký của một ngày cụ thể.</small>
          </span>
          <span class="export-type-option__check">
            <i v-if="selectedExportType === 'daily'" class="fa fa-check"></i>
          </span>
        </button>
      </div>
      <div
        v-if="selectedExportType === 'summary'"
        class="daily-export-form daily-export-form--inline"
      >
        <label>Tháng đăng ký</label>
        <el-date-picker
          v-model="selectedExportMonth"
          type="month"
          format="MM/yyyy"
          value-format="yyyy-MM"
          placeholder="Chọn tháng"
        />
      </div>
      <div
        v-if="selectedExportType === 'daily'"
        class="daily-export-form daily-export-form--inline"
      >
        <label>Ngày đăng ký</label>
        <el-date-picker
          v-model="selectedExportDate"
          type="date"
          format="dd/MM/yyyy"
          value-format="yyyy-MM-dd"
          placeholder="Chọn ngày"
        />
      </div>
      <span slot="footer" class="export-type-footer">
        <el-button :disabled="exporting" @click="exportTypeDialogVisible = false">
          Hủy
        </el-button>
        <el-button
          type="primary"
          :loading="exporting"
          :disabled="
            !selectedExportType ||
            (selectedExportType === 'summary' && !selectedExportMonth) ||
            (selectedExportType === 'daily' && !selectedExportDate)
          "
          @click="confirmExportType"
        >
          Xuất Excel
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="CHI TIẾT ĐĂNG KÝ"
      :visible.sync="registrationDetailVisible"
      width="560px"
      custom-class="lunch-list-dialog"
    >
      <div v-if="selectedRegistrationDetail" class="registration-detail-panel">
        <div class="dialog-meta">
          <strong>{{ selectedRegistrationDetail.employee.code }} - {{ selectedRegistrationDetail.employee.name }}</strong>
          <span>{{ selectedRegistrationDetail.department.name }} | {{ formatDate(selectedRegistrationDetail.registration.date) }}</span>
        </div>
        <div class="dialog-food-list">
          <div
            v-for="food in selectedRegistrationDetail.foods"
            :key="food.id"
            class="dialog-food-row"
          >
            <span>{{ food.name }}</span>
            <span>x{{ food.quantity }}</span>
            <strong>{{ formatCurrency(food.total) }}</strong>
          </div>
        </div>
        <div class="dialog-note">
          <span>Ghi chú</span>
          <strong>{{ selectedRegistrationDetail.registration.note || "-" }}</strong>
        </div>
        <div class="dialog-total">
          <span>Tổng tiền</span>
          <strong>{{ formatCurrency(selectedRegistrationDetail.total) }}</strong>
        </div>
      </div>
    </el-dialog>

    <el-dialog
      :title="foodOrderersTitle"
      :visible.sync="foodOrderersVisible"
      width="720px"
      custom-class="lunch-list-dialog"
    >
      <div class="food-orderers-panel">
        <p class="dialog-muted">
          {{ foodOrderersDate }} | {{ selectedFoodOrderers.length }} người đăng ký
        </p>
        <table class="lunch-dialog-table">
          <thead>
            <tr>
              <th>Mã NV</th>
              <th>Họ và tên</th>
              <th>Phòng ban</th>
              <th>Ghi chú</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in selectedFoodOrderers" :key="item.registration.id">
              <td>{{ item.employee.code }}</td>
              <td>{{ item.employee.name }}</td>
              <td>{{ item.department.name }}</td>
              <td>{{ item.registration.note || "-" }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import LunchApi from "./lunchApiService";
import {
  buildLunchDailyExportData,
  buildLunchExportData,
  exportLunchRegistrationDailyExcel,
  exportLunchRegistrationExcel
} from "./lunchExcelExporter";

const SUMMARY_BY_DAY = "day";
const SUMMARY_BY_MONTH = "month";
const ALL_DEPARTMENTS = "all";
const DEPARTMENT_DAILY_MODE = "daily";
const DEPARTMENT_DETAIL_MODE = "detail";
const SUMMARY_DEPARTMENT_MODE = "department";
const SUMMARY_FOOD_MODE = "food";

export default {
  name: "LunchRegistrationList",
  data() {
    const today = new Date();
    return {
      SUMMARY_BY_DAY,
      SUMMARY_BY_MONTH,
      ALL_DEPARTMENTS,
      DEPARTMENT_DAILY_MODE,
      DEPARTMENT_DETAIL_MODE,
      SUMMARY_DEPARTMENT_MODE,
      SUMMARY_FOOD_MODE,
      summaryPeriod: SUMMARY_BY_MONTH,
      selectedDepartmentId: ALL_DEPARTMENTS,
      selectedMonth: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`,
      selectedDate: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`,
      searchKeyword: "",
      loading: false,
      exporting: false,
      exportTypeDialogVisible: false,
      selectedExportType: "",
      selectedExportMonth: "",
      selectedExportDate: "",
      loadRequestId: 0,
      registrations: [],
      departmentOptions: [],
      departmentViewMode: DEPARTMENT_DAILY_MODE,
      summaryViewMode: SUMMARY_DEPARTMENT_MODE,
      registrationDetailVisible: false,
      selectedRegistrationDetail: null,
      foodOrderersVisible: false,
      selectedFoodOrderers: [],
      selectedFood: null,
      selectedFoodDate: ""
    };
  },
  computed: {
    departments() {
      return this.departmentOptions;
    },
    displayedDepartments() {
      if (this.selectedDepartmentId === ALL_DEPARTMENTS) return this.departments;
      return this.departments.filter(
        (department) => department.id === this.selectedDepartmentId
      );
    },
    isSummaryTab() {
      return this.summaryPeriod === SUMMARY_BY_MONTH;
    },
    isDepartmentTab() {
      return !this.isSummaryTab;
    },
    currentModes() {
      if (this.isSummaryTab) {
        return [
          { value: SUMMARY_DEPARTMENT_MODE, label: "Theo phòng ban" },
          { value: SUMMARY_FOOD_MODE, label: "Theo món ăn" }
        ];
      }
      return [
        { value: DEPARTMENT_DAILY_MODE, label: "Theo nhân viên" },
        { value: DEPARTMENT_DETAIL_MODE, label: "Theo món ăn" }
      ];
    },
    currentMode() {
      return this.isSummaryTab ? this.summaryViewMode : this.departmentViewMode;
    },
    monthRegistrations() {
      if (this.selectedDepartmentId === ALL_DEPARTMENTS) return this.registrations;
      return this.registrations.filter(
        (registration) =>
          (registration.departmentId || "unknown") === this.selectedDepartmentId
      );
    },
    hasMonthData() {
      return this.monthRegistrations.length > 0;
    },
    activeDepartment() {
      return this.departments.find(
        (department) => department.id === this.selectedDepartmentId
      );
    },
    selectedDepartmentLabel() {
      return this.activeDepartment ? this.activeDepartment.name : "Tất cả phòng ban";
    },
    departmentEmployees() {
      if (
        this.selectedDepartmentId !== ALL_DEPARTMENTS &&
        !this.activeDepartment
      ) return [];
      const employeeMap = new Map();
      this.monthRegistrations.forEach((registration) => {
        employeeMap.set(registration.employeeId, {
          id: registration.employeeId,
          code: registration.employeeNo,
          name: registration.employeeName,
          departmentId: registration.departmentId
        });
      });
      return Array.from(employeeMap.values()).sort((a, b) =>
        `${a.code}-${a.name}`.localeCompare(`${b.code}-${b.name}`)
      );
    },
    filteredDepartmentEmployees() {
      const searchText = this.normalizeText(this.searchKeyword);
      if (!searchText) return this.departmentEmployees;
      return this.departmentEmployees.filter((employee) =>
        this.normalizeText(`${employee.code} ${employee.name}`).includes(searchText)
      );
    },
    monthDays() {
      if (!this.selectedMonth) return [];
      const [year, month] = this.selectedMonth.split("-").map(Number);
      const dayCount = new Date(year, month, 0).getDate();
      return Array.from({ length: dayCount }, (_, index) => {
        const day = index + 1;
        const date = new Date(year, month - 1, day);
        const weekdayIndex = date.getDay();
        return {
          day,
          dateKey: `${this.selectedMonth}-${String(day).padStart(2, "0")}`,
          weekday: this.getShortWeekday(weekdayIndex),
          label: `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}`,
          isWeekend: weekdayIndex === 0 || weekdayIndex === 6
        };
      });
    },
    registeredDateKeys() {
      return [...new Set(this.monthRegistrations.map((registration) => registration.date))]
        .sort();
    },
    departmentDetailRows() {
      const employeeIds = this.filteredDepartmentEmployees.map((employee) => employee.id);
      return this.monthRegistrations
        .filter(
          (registration) =>
            registration.date === this.selectedDate &&
            employeeIds.includes(registration.employeeId)
        )
        .map((registration) => {
          const employee = this.getEmployeeById(registration.employeeId);
          const foods = this.getRegistrationFoods(registration);
          return {
            registration,
            employee,
            foods,
            foodNames: foods
              .map((food) => `${food.name}${food.quantity > 1 ? ` x${food.quantity}` : ""}`)
              .join(", "),
            total: foods.reduce((sum, food) => sum + food.total, 0)
          };
        })
        .filter((row) => row.employee)
        .sort((a, b) =>
          `${a.registration.date}-${a.employee.code}`.localeCompare(
            `${b.registration.date}-${b.employee.code}`
          )
        );
    },
    detailEmptyText() {
      if (!this.departmentEmployees.length) return "Chưa có nhân viên trong phòng ban này.";
      if (!this.filteredDepartmentEmployees.length) return "Không tìm thấy nhân viên phù hợp.";
      return "Chưa có dữ liệu đăng ký cơm trong tháng này.";
    },
    summaryExportData() {
      return buildLunchExportData(
        this.monthRegistrations,
        this.displayedDepartments,
        this.selectedMonth
      );
    },
    dailyViewData() {
      return buildLunchDailyExportData(
        this.monthRegistrations,
        this.selectedDate
      );
    },
    dailyDisplayRows() {
      const searchText = this.normalizeText(this.searchKeyword);
      const rows = this.dailyViewData.rows.filter(row =>
        !searchText ||
        this.normalizeText(
          `${row.employeeNo || ""} ${row.employeeName}`
        ).includes(searchText)
      );
      return rows.reduce((result, row, index) => {
        if (index > 0 && row.departmentName !== rows[index - 1].departmentName) {
          result.push({
            key: `separator-${row.departmentName}-${index}`,
            isSeparator: true
          });
        }
        result.push({
          key: `${row.employeeId}-${index}`,
          index: index + 1,
          row,
          isSeparator: false
        });
        return result;
      }, []);
    },
    dailyEmployeeCount() {
      return this.dailyDisplayRows.filter(item => !item.isSeparator).length;
    },
    dailyFoodRows() {
      return this.dailyViewData.foodRows;
    },
    dailyFoodTotals() {
      return this.dailyViewData.foodTotals;
    },
    summaryDepartmentRows() {
      return this.registeredDateKeys.map((dateKey) => {
        const departments = this.displayedDepartments.reduce((result, department) => {
          const count = this.monthRegistrations.filter((registration) => {
            return (
              registration.date === dateKey &&
              (registration.departmentId || "unknown") === department.id
            );
          }).length;
          return {
            ...result,
            [department.id]: count
          };
        }, {});
        return {
          dateKey,
          total: Object.values(departments).reduce((sum, count) => sum + count, 0),
          departments
        };
      });
    },
    summaryDepartmentTotals() {
      return this.summaryDepartmentRows.reduce(
        (result, row) => {
          this.displayedDepartments.forEach((department) => {
            result.departments[department.id] =
              (result.departments[department.id] || 0) + (row.departments[department.id] || 0);
          });
          result.total += row.total;
          return result;
        },
        { total: 0, departments: {} }
      );
    },
    monthFoods() {
      const foodMap = new Map();
      this.monthRegistrations.forEach((registration) => {
        this.getRegistrationFoods(registration).forEach((food) => {
          foodMap.set(food.id, food);
        });
      });
      return Array.from(foodMap.values()).sort((a, b) => a.id - b.id);
    },
    summaryFoodRows() {
      return this.registeredDateKeys.map((dateKey) => {
        const foods = this.monthFoods.reduce((result, food) => {
          return {
            ...result,
            [food.id]: this.getFoodDailyCount(food.id, dateKey)
          };
        }, {});
        return {
          dateKey,
          foods
        };
      });
    },
    dailyTableStyle() {
      return {
        minWidth: `${450 + this.monthDays.length * 56 + 76}px`
      };
    },
    foodSummaryTableStyle() {
      return {
        minWidth: `${130 + Math.max(this.monthFoods.length, 1) * 150}px`
      };
    },
    foodOrderersTitle() {
      return this.selectedFood ? `DANH SÁCH ĐẶT ${this.selectedFood.name.toUpperCase()}` : "";
    },
    foodOrderersDate() {
      return this.selectedFoodDate ? this.formatDate(this.selectedFoodDate) : "";
    }
  },
  watch: {
    selectedMonth() {
      this.loadListData();
    },
    selectedDate(value) {
      const month = String(value || "").slice(0, 7);
      if (month && month !== this.selectedMonth) {
        this.selectedMonth = month;
      }
    }
  },
  created() {
    this.loadListData();
  },
  methods: {
    async loadListData() {
      const requestId = this.loadRequestId + 1;
      this.loadRequestId = requestId;
      if (!this.selectedMonth) {
        this.registrations = [];
        this.departmentOptions = [];
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const [response, departments] = await Promise.all([
          LunchApi.listRegistrations({
            month: this.selectedMonth,
            status: "REGISTERED",
            limit: 1000000
          }),
          LunchApi.listDepartments()
        ]);
        if (requestId !== this.loadRequestId) return;
        this.registrations = response.result || [];
        this.departmentOptions = this.buildDepartmentsFromRegistrations(this.registrations, departments);
        if (
          this.selectedDepartmentId !== ALL_DEPARTMENTS &&
          !this.departmentOptions.some(
            (department) => department.id === this.selectedDepartmentId
          )
        ) {
          this.selectedDepartmentId = ALL_DEPARTMENTS;
        }
      } catch (error) {
        if (requestId !== this.loadRequestId) return;
        this.registrations = [];
        this.departmentOptions = [];
        this.showApiError(error, "Không tải được danh sách đăng ký cơm.");
      } finally {
        if (requestId === this.loadRequestId) {
          this.loading = false;
        }
      }
    },
    buildDepartmentsFromRegistrations(registrations, departments = []) {
      const departmentMap = new Map();
      departments.forEach((department) => {
        if (department.id) {
          departmentMap.set(department.id, {
            id: department.id,
            name: department.name || department.code || "Chưa xác định"
          });
        }
      });
      registrations.forEach((registration) => {
        const id = registration.departmentId || "unknown";
        if (!departmentMap.has(id)) {
          departmentMap.set(id, {
            id,
            name: registration.departmentName || "Chưa xác định"
          });
        }
      });
      return Array.from(departmentMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    },
    selectSummaryPeriod(period) {
      this.summaryPeriod = period;
      this.searchKeyword = "";
      if (
        period === SUMMARY_BY_DAY &&
        !String(this.selectedDate || "").startsWith(`${this.selectedMonth}-`)
      ) {
        const firstRegistration = this.monthRegistrations[0];
        this.selectedDate = firstRegistration
          ? firstRegistration.date
          : `${this.selectedMonth}-01`;
      }
    },
    selectDepartment(departmentId) {
      this.selectedDepartmentId = departmentId;
      this.searchKeyword = "";
    },
    setCurrentMode(mode) {
      if (this.isSummaryTab) {
        this.summaryViewMode = mode;
        return;
      }
      this.departmentViewMode = mode;
    },
    goBackToRegistration() {
      this.$router.push({ path: "/admin/lunch-registration" }).catch(() => {});
    },
    openExportTypeDialog() {
      if (this.exporting || this.loading) return;
      if (!this.selectedMonth) {
        if (this.$message) {
          this.$message.warning("Vui lòng chọn tháng cần xem.");
        }
        return;
      }
      this.selectedExportType = "";
      this.selectedExportMonth = this.selectedMonth;
      this.exportTypeDialogVisible = true;
    },
    selectExportType(type) {
      this.selectedExportType = type;
      if (type === "daily") {
        this.prepareDailyExport();
      }
    },
    async confirmExportType() {
      if (!this.selectedExportType || this.exporting || this.loading) return;
      if (this.selectedExportType === "daily") {
        await this.exportDailyExcel();
        return;
      }
      if (this.selectedExportType === "summary") {
        const exported = await this.exportSummaryExcel();
        if (exported) this.exportTypeDialogVisible = false;
      }
    },
    prepareDailyExport() {
      const today = new Date();
      const todayKey = `${today.getFullYear()}-${String(
        today.getMonth() + 1
      ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      const registrationDates = [
        ...new Set(this.monthRegistrations.map(registration => registration.date))
      ].sort();
      this.selectedExportDate = todayKey.startsWith(`${this.selectedMonth}-`)
        ? todayKey
        : registrationDates[0] || `${this.selectedMonth}-01`;
    },
    async exportSummaryExcel() {
      if (this.exporting || this.loading) return false;
      if (!this.selectedExportMonth) {
        if (this.$message) {
          this.$message.warning("Vui lòng chọn tháng cần xuất Excel.");
        }
        return false;
      }

      this.exporting = true;
      try {
        let exportRegistrations;
        if (this.selectedExportMonth === this.selectedMonth) {
          exportRegistrations = this.monthRegistrations;
        } else {
          const response = await LunchApi.listRegistrations({
            month: this.selectedExportMonth,
            status: "REGISTERED",
            limit: 1000000
          });
          exportRegistrations = (response.result || []).filter(
            registration =>
              this.selectedDepartmentId === ALL_DEPARTMENTS ||
              (registration.departmentId || "unknown") === this.selectedDepartmentId
          );
        }

        if (!exportRegistrations.length) {
          if (this.$message) {
            this.$message.warning(
              "Không có dữ liệu đăng ký cơm trong tháng đã chọn."
            );
          }
          return false;
        }

        await exportLunchRegistrationExcel(
          exportRegistrations,
          this.displayedDepartments,
          this.selectedExportMonth,
          { departmentName: this.selectedDepartmentLabel }
        );
        if (this.$message) {
          this.$message.success("Xuất Excel danh sách đăng ký cơm thành công.");
        }
        return true;
      } catch (error) {
        this.showApiError(error, "Không thể xuất Excel danh sách đăng ký cơm.");
        return false;
      } finally {
        this.exporting = false;
      }
    },
    async exportDailyExcel() {
      if (this.exporting || this.loading) return;
      if (!this.selectedExportDate) {
        if (this.$message) {
          this.$message.warning("Vui lòng chọn ngày cần xuất Excel.");
        }
        return;
      }

      this.exporting = true;
      try {
        let dailyRegistrations;
        if (this.selectedExportDate.startsWith(`${this.selectedMonth}-`)) {
          dailyRegistrations = this.monthRegistrations.filter(
            registration =>
              registration.date === this.selectedExportDate &&
              (!registration.status || registration.status === "REGISTERED")
          );
        } else {
          const response = await LunchApi.listRegistrations({
            date: this.selectedExportDate,
            status: "REGISTERED",
            limit: 1000000
          });
          dailyRegistrations = (response.result || []).filter(
            registration =>
              this.selectedDepartmentId === ALL_DEPARTMENTS ||
              (registration.departmentId || "unknown") === this.selectedDepartmentId
          );
        }

        if (!dailyRegistrations.length) {
          if (this.$message) {
            this.$message.warning(
              "Không có dữ liệu đăng ký cơm trong ngày đã chọn."
            );
          }
          return;
        }

        await exportLunchRegistrationDailyExcel(
          dailyRegistrations,
          this.selectedExportDate,
          { departmentName: this.selectedDepartmentLabel }
        );
        this.exportTypeDialogVisible = false;
        if (this.$message) {
          this.$message.success(
            "Xuất Excel danh sách đăng ký cơm theo ngày thành công."
          );
        }
      } catch (error) {
        this.showApiError(
          error,
          "Không thể xuất Excel danh sách đăng ký cơm theo ngày."
        );
      } finally {
        this.exporting = false;
      }
    },
    getEmployeeDayRegistration(employeeId, dateKey) {
      return this.monthRegistrations.find(
        (registration) => registration.employeeId === employeeId && registration.date === dateKey
      );
    },
    getEmployeeMonthlyRegistrationCount(employeeId) {
      return this.monthRegistrations.filter((registration) => registration.employeeId === employeeId).length;
    },
    openRegistrationDetail(registration) {
      const employee = this.getEmployeeById(registration.employeeId);
      if (!employee) return;
      const department = this.departments.find((item) => item.id === employee.departmentId) || {
        id: employee.departmentId,
        name: registration.departmentName || "Chưa xác định"
      };
      const foods = this.getRegistrationFoods(registration);
      this.selectedRegistrationDetail = {
        registration,
        employee,
        department,
        foods,
        total: foods.reduce((sum, food) => sum + food.total, 0)
      };
      this.registrationDetailVisible = true;
    },
    openFoodOrderers(food, dateKey, count) {
      if (!count) return;
      this.selectedFood = food;
      this.selectedFoodDate = dateKey;
      this.selectedFoodOrderers = this.monthRegistrations
        .filter((registration) =>
          registration.date === dateKey &&
          registration.items.some((item) => item.foodId === food.id)
        )
        .map((registration) => ({
          registration,
          employee: this.getEmployeeById(registration.employeeId),
          department: this.departments.find((department) => department.id === registration.departmentId) || {
            id: registration.departmentId,
            name: registration.departmentName || "Chưa xác định"
          }
        }));
      this.foodOrderersVisible = true;
    },
    getEmployeeById(employeeId) {
      const registration = this.monthRegistrations.find((item) => item.employeeId === employeeId);
      if (!registration) return null;
      return {
        id: registration.employeeId,
        code: registration.employeeNo,
        name: registration.employeeName,
        departmentId: registration.departmentId
      };
    },
    getRegistrationFoods(registration) {
      if (!registration) return [];
      return (registration.items || []).map((item) => ({
        id: item.foodId,
        name: item.foodName,
        category: item.category,
        quantity: item.quantity || 1,
        total: item.total || item.price * (item.quantity || 1)
      }));
    },
    getFoodDailyCount(foodId, dateKey) {
      return this.monthRegistrations
        .filter((registration) => registration.date === dateKey)
        .reduce((sum, registration) => {
          const item = registration.items.find((food) => food.foodId === foodId);
          return sum + (item ? item.quantity || 1 : 0);
        }, 0);
    },
    normalizeText(value) {
      return String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },
    showApiError(error, fallback) {
      const message = (error && error.result && error.result.message) || (error && error.message) || fallback;
      if (this.$message) {
        this.$message.error(message);
      }
    },
    formatDate(dateKey) {
      const [year, month, day] = dateKey.split("-");
      return `${day}/${month}/${year}`;
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("vi-VN").format(value || 0) + " đ";
    },
    getShortWeekday(weekdayIndex) {
      return ["CN", "T2", "T3", "T4", "T5", "T6", "T7"][weekdayIndex];
    }
  }
};
</script>

<style scoped lang="scss">
.lunch-registration-list-page {
  box-sizing: border-box;
  min-height: 100%;
  padding: 20px 10px 18px 24px;
  width: 100%;

  > .card {
    background: transparent;
    border: 0;
    box-shadow: none;
    margin: 0;
  }

  .main-card-body {
    padding: 0;
  }
}

.lunch-list-header {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 18px;

  .card-header-title {
    color: #1f2937;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 1.25;
    margin: 0;
  }
}

.lunch-list-subtitle {
  color: #667085;
  font-size: 16px;
  line-height: 1.45;
  margin: 4px 0 0;
}

.lunch-list-header__actions {
  display: flex;
  flex: 0 0 auto;
  gap: 10px;
}

.lunch-primary-button {
  align-items: center;
  background: #fb985b;
  border: 1px solid #fb985b;
  border-radius: 8px;
  color: #ffffff;
  display: inline-flex;
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
  height: 42px;
  justify-content: center;
  line-height: 1;
  padding: 0 18px;
  white-space: nowrap;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }

  &:hover {
    background: #ff8842;
    border-color: #ff8842;
  }
}

.lunch-primary-button--outline {
  background: #ffffff;
  color: #fb985b;

  &:hover {
    background: #fff4ed;
    color: #fb7f35;
  }
}

.lunch-list-card {
  background: #ffffff;
  border: 1px solid #e5e9f0;
  border-radius: 8px;
  box-shadow: 0 12px 28px rgba(17, 24, 39, 0.05);
  min-width: 0;
  padding: 20px;
}

.lunch-section-filter {
  align-items: flex-end;
  border-bottom: 1px solid #edf0f4;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: -2px -2px 18px;
  padding: 2px 2px 16px;
}

.lunch-section-filter__field {
  align-items: center;
  display: flex;
  gap: 10px;
  width: 280px;

  label {
    color: #344054;
    flex: 0 0 auto;
    font-size: 14px;
    margin: 0;
    white-space: nowrap;
  }
}

.lunch-section-filter__field--department {
  width: 420px;
}

.lunch-section-select {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}

.lunch-mode-button {
  background: #ffffff;
  border: 1px solid #dfe4ea;
  border-radius: 999px;
  color: #344054;
  font-size: 15px;
  line-height: 1;
  padding: 12px 18px;
  white-space: nowrap;

  &:hover {
    border-color: #fb985b;
    color: #fb985b;
  }
}

.lunch-mode-button--active {
  background: #fff1e8;
  border-color: #fb985b;
  color: #fb7f35;
}

.lunch-list-toolbar {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 18px;
}

.lunch-mode-switch {
  display: flex;
  flex: 1 1 auto;
  gap: 8px;
  min-width: 0;
  overflow-x: auto;
  white-space: nowrap;
}

.lunch-list-filters {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 12px;
}

.lunch-month-picker {
  width: 150px;
}

.lunch-date-picker {
  width: 190px;
}

.lunch-employee-search {
  width: 320px;
}

.lunch-data-grid-scroll {
  border: 1px solid #e1e6ed;
  border-radius: 8px;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

.lunch-data-grid {
  border-collapse: separate;
  border-spacing: 0;
  color: #1f2937;
  font-size: 15px;
  table-layout: fixed;
  width: 100%;

  th,
  td {
    border-bottom: 1px solid #e6ebf2;
    border-right: 1px solid #e6ebf2;
    height: 54px;
    padding: 10px 12px;
    text-align: center;
    vertical-align: middle;
  }

  th {
    background: #fb985b;
    color: #ffffff;
    font-weight: 700;
  }

  tbody tr:nth-child(even) td {
    background: #fafafa;
  }

  tbody tr:hover td {
    background: #fff7f0;
  }

  tfoot td {
    background: #fff1e8;
    color: #1f2937;
    font-weight: 700;
  }
}

.lunch-daily-grid {
  .sticky-col,
  .sticky-total {
    position: sticky;
    z-index: 2;
  }

  th.sticky-col,
  th.sticky-total {
    z-index: 4;
  }

  .sticky-stt {
    left: 0;
    width: 56px;
  }

  .sticky-code {
    left: 56px;
    width: 92px;
  }

  .sticky-name {
    left: 148px;
    width: 220px;
  }

  .sticky-total {
    right: 0;
    width: 76px;
  }

  tbody .sticky-col,
  tbody .sticky-total {
    background: #ffffff;
  }

  tbody tr:nth-child(even) .sticky-col,
  tbody tr:nth-child(even) .sticky-total {
    background: #fafafa;
  }

  tbody tr:hover .sticky-col,
  tbody tr:hover .sticky-total {
    background: #fff7f0;
  }
}

.day-header {
  width: 56px;

  span,
  strong {
    display: block;
    line-height: 1.2;
  }

  span {
    font-size: 12px;
    opacity: 0.9;
  }
}

.is-weekend {
  color: #98a2b3;
}

th.is-weekend {
  background: #f5a86e;
  color: #ffffff;
}

.daily-cell {
  width: 56px;
}

.registration-check {
  align-items: center;
  background: #ecfdf3;
  border: 1px solid #b7e4c7;
  border-radius: 50%;
  color: #12a150;
  cursor: pointer;
  display: inline-flex;
  font-size: 17px;
  font-weight: 800;
  height: 28px;
  justify-content: center;
  line-height: 1;
  width: 28px;

  &:hover {
    background: #d8f8e5;
    border-color: #12a150;
  }
}

.lunch-detail-grid {
  min-width: 960px;

  th:nth-child(1) { width: 120px; }
  th:nth-child(2) { width: 110px; }
  th:nth-child(3) { width: 220px; }
  th:nth-child(5) { width: 220px; }
  th:nth-child(6) { width: 130px; }
}

.lunch-daily-food-grid {
  min-width: 680px;

  th:nth-child(1) { width: 70px; }
  th:nth-child(2) { width: 50%; }
  th:nth-child(3) { width: 180px; }
  th:nth-child(4) { width: 200px; }
}

.lunch-daily-food-scroll {
  max-height: 596px;
  overflow: auto;

  .lunch-daily-food-grid thead th {
    position: sticky;
    top: 0;
    z-index: 2;
  }

  .lunch-daily-food-grid tfoot td {
    bottom: 0;
    position: sticky;
    z-index: 2;
  }
}

.lunch-summary-grid {
  min-width: 720px;

  th:nth-child(1) { width: 70px; }
  th:nth-child(2) { width: 30%; }
  th:nth-child(3),
  th:nth-child(4),
  th:nth-child(5) { width: 20%; }
}

.lunch-summary-food-grid {
  min-width: 620px;

  th:nth-child(1) { width: 70px; }
  th:nth-child(2) { width: 48%; }
  th:nth-child(3),
  th:nth-child(4) { width: 26%; }
}

.clickable-row {
  cursor: pointer;
}

.food-count-button {
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  font: inherit;
  padding: 0;

  &:hover {
    color: inherit;
  }
}

.text-left {
  text-align: left !important;
}

.text-right {
  text-align: right !important;
}

.lunch-list-empty,
.lunch-table-empty-cell {
  color: #667085;
  padding: 34px 16px !important;
  text-align: center !important;
}

.lunch-list-dialog {
  border-radius: 10px;
}

.export-type-options {
  display: grid;
  gap: 12px;
}

.export-type-option {
  align-items: center;
  background: #ffffff;
  border: 1px solid #dfe4ea;
  border-radius: 8px;
  color: #1f2937;
  display: flex;
  gap: 14px;
  padding: 16px;
  text-align: left;
  width: 100%;

  > i {
    align-items: center;
    background: #fff1e8;
    border-radius: 8px;
    color: #fb7f35;
    display: inline-flex;
    flex: 0 0 42px;
    font-size: 19px;
    height: 42px;
    justify-content: center;
  }

  .export-type-option__content {
    display: grid;
    flex: 1;
    gap: 4px;
    min-width: 0;
  }

  strong {
    font-size: 15px;
  }

  small {
    color: #667085;
    line-height: 1.4;
  }

  &:hover {
    background: #fffaf6;
    border-color: #fb985b;
    cursor: pointer;
  }

  &.is-selected {
    background: #fff8f3;
    border-color: #fb7f35;
    box-shadow: 0 0 0 2px rgba(251, 127, 53, 0.12);
  }
}

.export-type-option__check {
  align-items: center;
  border: 1px solid #d7dde5;
  border-radius: 50%;
  display: inline-flex;
  flex: 0 0 22px;
  height: 22px;
  justify-content: center;
  width: 22px;

  .is-selected & {
    background: #fb7f35;
    border-color: #fb7f35;
    color: #ffffff;
    font-size: 11px;
  }
}

.export-type-footer {
  display: flex;
  gap: 10px;
  justify-content: flex-end;

  ::v-deep .el-button {
    min-width: 110px;
  }
}

.daily-export-form {
  display: grid;
  gap: 8px;

  label {
    color: #344054;
    font-weight: 600;
    margin: 0;
  }

  small {
    color: #667085;
  }

  ::v-deep .el-date-editor {
    width: 100%;
  }
}

.daily-export-form--inline {
  border: 1px solid #f4d7c5;
  border-radius: 8px;
  background: #fffaf6;
  margin-top: 14px;
  padding: 14px 16px;
}

.dialog-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;

  strong {
    color: #1f2937;
    font-size: 17px;
  }

  span {
    color: #667085;
  }
}

.dialog-food-list {
  border: 1px solid #e6ebf2;
  border-radius: 8px;
  overflow: hidden;
}

.dialog-food-row,
.dialog-note,
.dialog-total {
  align-items: center;
  border-bottom: 1px solid #e6ebf2;
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr 56px 120px;
  padding: 12px 14px;

  &:last-child {
    border-bottom: 0;
  }
}

.dialog-note,
.dialog-total {
  border-bottom: 0;
  grid-template-columns: 120px 1fr;
  margin-top: 12px;
}

.dialog-note span,
.dialog-total span,
.dialog-muted {
  color: #667085;
}

.dialog-total {
  background: #fff7f0;
  border-radius: 8px;
  color: #1f2937;
  font-size: 17px;
}

.lunch-dialog-table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;

  th,
  td {
    border-bottom: 1px solid #e6ebf2;
    padding: 12px;
    text-align: left;
  }

  th {
    background: #fff1e8;
    color: #1f2937;
    font-weight: 700;
  }
}

::v-deep .lunch-check-tooltip {
  background: #ffffff !important;
  border: 1px solid #d9dee7 !important;
  color: #1f2937 !important;

  .popper__arrow,
  .popper__arrow::after {
    border-top-color: #ffffff !important;
  }
}

@media (max-width: 1199px) {
  .lunch-list-header,
  .lunch-list-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .lunch-list-header__actions,
  .lunch-list-filters {
    justify-content: flex-start;
  }

}

.lunch-daily-summary {
  align-items: center;
  background: #fff7f0;
  border-bottom: 1px solid #e1e6ed;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 28px;
  padding: 12px 16px;

  span {
    color: #667085;
    font-size: 14px;
  }

  strong {
    color: #1f2937;
  }
}

.lunch-daily-export-grid {
  min-width: 900px;

  th:nth-child(1),
  td:nth-child(1) { width: 70px; }
  th:nth-child(2),
  td:nth-child(2) { width: 210px; }
  th:nth-child(3),
  td:nth-child(3) { width: 230px; }
  th:nth-child(4),
  td:nth-child(4) { width: 280px; }
  th:nth-child(5),
  td:nth-child(5) { width: 240px; }

  .food-text-cell {
    white-space: pre-line;
  }

  .department-separator-row td {
    background: #fff1e8 !important;
    border-bottom: 2px solid #7f8c9f;
    border-top: 2px solid #7f8c9f;
    height: 14px;
    padding: 0;
  }
}

.lunch-daily-employee-scroll.has-scroll {
  overflow-x: auto;
  overflow-y: hidden;

  .lunch-daily-export-grid thead {
    box-shadow: 8px 0 0 #fb985b;
    display: table;
    table-layout: fixed;
    width: calc(100% - 8px);
  }

  .lunch-daily-export-grid tbody {
    display: block;
    max-height: 551px;
    overflow-y: auto;
    scrollbar-gutter: stable;
    width: 100%;
  }

  .lunch-daily-export-grid tbody tr {
    display: table;
    table-layout: fixed;
    width: 100%;
  }

  .lunch-daily-export-grid tbody::-webkit-scrollbar {
    width: 8px;
  }

  .lunch-daily-export-grid tbody::-webkit-scrollbar-thumb {
    background: #fb985b;
    border-radius: 999px;
  }

  .lunch-daily-export-grid tbody::-webkit-scrollbar-track {
    background: #fff1e8;
  }
}

@media (max-width: 767px) {
  .lunch-registration-list-page {
    padding: 14px 8px;
  }

  .lunch-list-card {
    padding: 14px;
  }

  .lunch-list-header .card-header-title {
    font-size: 20px;
  }

  .lunch-list-subtitle {
    font-size: 14px;
  }

  .lunch-list-header__actions,
  .lunch-list-filters {
    flex-direction: column;
    width: 100%;
  }

  .lunch-primary-button,
  .lunch-section-filter__field,
  .lunch-month-picker,
  .lunch-employee-search {
    width: 100%;
  }
}
</style>
